import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

/**
 * The statistics row between the toolbar and the filters.
 *
 * Measurements are APR's, taken from its stylesheet rather than from a screenshot:
 *
 *   .master-data-stats      flex · gap 1.4rem · padding 0.7rem · #fff
 *                           · 1px #dee2e6 · radius 0.35rem
 *   .stat-item              column · centred · radius 8px · #fafafa
 *                           · 1px #e0e0e0            (a later rule in APR's cascade
 *                                                     overrides the borderless one)
 *   .master-data-stats
 *     .stat-item            padding 0.35rem 0.7rem   (wins on specificity)
 *     .stat-value           0.875rem · 600 · #2c3e50 · line-height 1
 *     .stat-label           0.65rem · #6c757d · margin-top 0.175rem
 *
 * Figures are of the FILTERED set, so they answer "what am I looking at" rather than
 * "what exists". The first label flips between Total and Filtered to say which.
 */

export interface Stat {
  readonly label: string;
  readonly value: string;
  /** Dimmed, with a tooltip, when the underlying column is not migrated yet. */
  readonly pending?: string;
}

export function StatsStrip({ stats }: { stats: readonly Stat[] }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '22.4px',
        p: '11.2px',
        mb: '0.75rem',
        borderRadius: '5.6px',
        border: '1.25px solid',
        borderColor: '#dee2e6',
        bgcolor: 'background.paper',
      }}
    >
      {stats.map((stat) => (
        <Box
          key={stat.label}
          {...(stat.pending ? { title: stat.pending } : {})}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            px: '11.2px',
            py: '5.6px',
            borderRadius: '8px',
            border: '1.25px solid',
            borderColor: '#e0e0e0',
            // Sits a shade back from the strip it lives in.
            bgcolor: (t) => (t.palette.mode === 'dark' ? 'action.hover' : '#fafafa'),
            transition: 'box-shadow 0.2s',
            '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
              color: stat.pending ? 'text.disabled' : 'text.primary',
            }}
          >
            {stat.value}
          </Typography>
          <Typography
            sx={{
              fontSize: 10.4,
              fontWeight: 500,
              mt: '2.8px',
              color: '#6c757d',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap',
            }}
          >
            {stat.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
